import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { FC, Fragment, ReactNode, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

interface ModalProps {
    open: boolean;
    setOpen: (isOpen: boolean) => void;
    title: string;
    description?: string | ReactNode;
    children: ReactNode;
    renderActions?: () => ReactNode;
    loading?: boolean;
    size?: "sm" | "md" | "lg" | "xl" | "6xl";
    onClose?: () => void;
}

const Modal: FC<ModalProps> = ({
    open,
    setOpen,
    title,
    description,
    children,
    renderActions,
    loading = false,
    size = "md",
    onClose,
}) => {
    const { t } = useTranslation();
    const wasOpen = useRef(false);

    useEffect(() => {
        if (wasOpen.current && !open && onClose) {
            onClose();
        }
        wasOpen.current = open;
    }, [open, onClose]);

    const sizeClasses = {
        sm: "max-w-sm",
        md: "max-w-md",
        lg: "max-w-lg",
        xl: "max-w-xl",
        "6xl": "max-w-6xl",
    };

    return (
        <Transition show={open} as={Fragment}>
            <Dialog
                as="div"
                className="relative z-50"
                onClose={() => setOpen(false)}>
                <TransitionChild
                    as="div"
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0">
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>

                <div className="fixed inset-0 z-10 overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <TransitionChild
                            as="div"
                            className="contents"
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
                            <DialogPanel
                                className={`relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full ${sizeClasses[size]} sm:p-6`}>
                                {loading && (
                                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-green-500"></div>
                                    </div>
                                )}
                                <div>
                                    <div className="flex items-start justify-between">
                                        <DialogTitle
                                            as="h3"
                                            className="text-lg font-medium leading-6 text-gray-900">
                                            {title}
                                        </DialogTitle>
                                        <div className="ml-3 flex h-7 items-center">
                                            <button
                                                type="button"
                                                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500"
                                                onClick={() => setOpen(false)}>
                                                <span className="sr-only">
                                                    {t(
                                                        "components.modal.close",
                                                    )}
                                                </span>
                                                <XMarkIcon
                                                    className="h-6 w-6"
                                                    aria-hidden="true"
                                                />
                                            </button>
                                        </div>
                                    </div>
                                    {description && (
                                        <div className="mt-2">
                                            {typeof description === "string" ? (
                                                <p className="text-sm text-gray-500">
                                                    {description}
                                                </p>
                                            ) : (
                                                <div className="text-sm text-gray-500">
                                                    {description}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="mt-5 sm:mt-6">{children}</div>
                                {renderActions && (
                                    <div className="mt-5 sm:mt-6 flex justify-end space-x-2">
                                        {renderActions()}
                                    </div>
                                )}
                            </DialogPanel>
                        </TransitionChild>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default Modal;
